/**
 * Agent Interface — abstract contract between the Offensive UI and any
 * backend agent (Kingpin agent, PentAGI, HexStrike, or mock simulation).
 *
 * The UI only talks to an adapter that implements this interface.
 * Swap the adapter and the UI works unchanged.
 *
 * Event shape follows the same contract as offensiveStore.handleWsEvent():
 *   { type: string, data: object }
 *
 * Event types:
 *   tactic_started       { tactic_id }
 *   tactic_completed     { tactic_id }
 *   technique_queued     { technique_id, tactic_id, name, command, tool, agent }
 *   technique_started    { technique_id, timestamp }
 *   technique_output     { technique_id, output }
 *   technique_completed  { technique_id, tactic_id, status, timestamp, findings[] }
 *   engagement_completed {}
 *   scan_progress        { phase, percent, message }
 *   finding              { ...finding data }
 *   error                { message }
 */

import { Config } from '../config';
import axios from 'axios';

/* ---------------------------------------------------------------------------
 * Base class — defines the contract. Adapters extend this.
 * ------------------------------------------------------------------------- */

export class AgentAdapter {
  constructor({ onEvent, onChatChunk, onError }) {
    this.onEvent = onEvent || (() => {});
    this.onChatChunk = onChatChunk || (() => {});
    this.onError = onError || ((e) => console.error('[Agent]', e));
  }

  async start(engagementId, config = {}) {
    throw new Error('start() not implemented');
  }

  async pause(executionId) {
    throw new Error('pause() not implemented');
  }

  async resume(executionId) {
    throw new Error('resume() not implemented');
  }

  async stop(executionId) {
    throw new Error('stop() not implemented');
  }

  async sendMessage(message, engagementContext = {}) {
    throw new Error('sendMessage() not implemented');
  }

  disconnect() {}
}


/* ---------------------------------------------------------------------------
 * MockAgentAdapter — simulates agent behaviour locally for development
 * and demos. Produces events on the same contract as a real backend.
 * ------------------------------------------------------------------------- */

const MOCK_TACTICS = [
  'TA0043', 'TA0042', 'TA0001', 'TA0002', 'TA0003', 'TA0004',
  'TA0005', 'TA0006', 'TA0007', 'TA0008', 'TA0009', 'TA0011',
  'TA0010', 'TA0040',
];

const MOCK_TECHNIQUE_NAMES = {
  TA0043: ['Active Scanning', 'Gather Victim Network Information'],
  TA0042: ['Acquire Infrastructure', 'Develop Capabilities'],
  TA0001: ['Exploit Public-Facing Application', 'Valid Accounts'],
  TA0002: ['Command and Scripting Interpreter', 'Exploitation for Client Execution'],
  TA0003: ['Boot or Logon Autostart Execution', 'Create Account'],
  TA0004: ['Exploitation for Privilege Escalation', 'Sudo and Sudo Caching'],
  TA0005: ['Obfuscated Files', 'Indicator Removal'],
  TA0006: ['Brute Force', 'OS Credential Dumping'],
  TA0007: ['Network Service Discovery', 'System Information Discovery'],
  TA0008: ['Remote Services', 'Lateral Tool Transfer'],
  TA0009: ['Data from Local System', 'Archive Collected Data'],
  TA0011: ['Application Layer Protocol', 'Encrypted Channel'],
  TA0010: ['Exfiltration Over C2 Channel'],
  TA0040: ['Data Destruction', 'Service Stop'],
};

const MOCK_OUTPUTS = [
  '$ nmap -sV -sC -p- 192.168.1.0/24\nStarting Nmap 7.94 ( https://nmap.org )\n',
  'Discovered host: 192.168.1.10 (open ports: 22, 80, 443, 3306)\n',
  'SERVICE DETECTION: Apache/2.4.54 on port 80\n',
  'VULNERABILITY: CVE-2023-25690 — mod_proxy HTTP Request Smuggling\n',
  'Attempting exploitation...\n',
  'SUCCESS: Remote code execution achieved via HTTP smuggling\n',
  'Establishing persistence... done\n',
  'Credential harvesting from /etc/shadow...\n',
  'Found 3 password hashes. Starting offline cracking...\n',
  'WARNING: Detected endpoint monitoring agent (EDR). Switching to evasion mode.\n',
];

export class MockAgentAdapter extends AgentAdapter {
  constructor(opts) {
    super(opts);
    this._timers = [];
    this._running = false;
    this._depth = 3;
  }

  async start(engagementId, config = {}) {
    this._running = true;
    this._depth = config.depth || 3;
    const tacticsToRun = MOCK_TACTICS.slice(0, this._depth);

    this.onEvent({ type: 'scan_progress', data: { phase: 'Initializing', percent: 0, message: 'Agent starting...' } });

    let delay = 800;
    for (let i = 0; i < tacticsToRun.length; i++) {
      const tacticId = tacticsToRun[i];
      const pct = Math.round(((i + 1) / tacticsToRun.length) * 100);

      this._schedule(delay, () => {
        if (!this._running) return;
        this.onEvent({ type: 'tactic_started', data: { tactic_id: tacticId } });
        this.onEvent({ type: 'scan_progress', data: { phase: tacticId, percent: pct - 10, message: `Executing ${tacticId}...` } });
      });
      delay += 1200;

      const techniques = MOCK_TECHNIQUE_NAMES[tacticId] || ['Unknown Technique'];
      for (const techName of techniques) {
        const techId = `${tacticId}-${techName.replace(/\s+/g, '_')}-${Date.now()}`;

        this._schedule(delay, () => {
          if (!this._running) return;
          this.onEvent({
            type: 'technique_queued',
            data: { technique_id: techId, tactic_id: tacticId, name: techName, command: `run ${techName.toLowerCase()}`, tool: 'mock-tool', agent: 'mock-agent' },
          });
        });
        delay += 400;

        this._schedule(delay, () => {
          if (!this._running) return;
          this.onEvent({ type: 'technique_started', data: { technique_id: techId, timestamp: new Date().toISOString() } });
        });
        delay += 300;

        for (let o = 0; o < 3; o++) {
          const line = MOCK_OUTPUTS[Math.floor(Math.random() * MOCK_OUTPUTS.length)];
          this._schedule(delay, () => {
            if (!this._running) return;
            this.onEvent({ type: 'technique_output', data: { technique_id: techId, output: line } });
          });
          delay += 200 + Math.random() * 400;
        }

        this._schedule(delay, () => {
          if (!this._running) return;
          const hasFindings = Math.random() > 0.6;
          this.onEvent({
            type: 'technique_completed',
            data: {
              technique_id: techId,
              tactic_id: tacticId,
              status: 'success',
              timestamp: new Date().toISOString(),
              findings: hasFindings
                ? [{ title: `Mock finding from ${techName}`, severity: ['Critical', 'High', 'Medium', 'Low'][Math.floor(Math.random() * 4)] }]
                : [],
            },
          });
        });
        delay += 600;
      }

      this._schedule(delay, () => {
        if (!this._running) return;
        this.onEvent({ type: 'tactic_completed', data: { tactic_id: tacticId } });
        this.onEvent({ type: 'scan_progress', data: { phase: tacticId, percent: pct, message: `${tacticId} completed` } });
      });
      delay += 500;
    }

    this._schedule(delay, () => {
      if (!this._running) return;
      this.onEvent({ type: 'scan_progress', data: { phase: 'Complete', percent: 100, message: 'Engagement finished' } });
      this.onEvent({ type: 'engagement_completed', data: {} });
      this._running = false;
    });

    return { executionId: `mock-${Date.now()}` };
  }

  async pause() {
    this._running = false;
    this._clearTimers();
  }

  async resume() {
    this._running = true;
  }

  async stop() {
    this._running = false;
    this._clearTimers();
    this.onEvent({ type: 'scan_progress', data: { phase: 'Stopped', percent: 0, message: 'Engagement stopped' } });
  }

  async sendMessage(message) {
    const responses = [
      'Based on the current scan results, I recommend focusing on the web application attack surface. The Apache server on port 80 shows potential for HTTP request smuggling.',
      'The credentials found in /etc/shadow can be cracked offline using hashcat with the rockyou wordlist. This is a common post-exploitation technique mapped to MITRE ATT&CK T1110.',
      'For lateral movement, try exploiting the open SMB port on 192.168.1.15. The Eternal Blue exploit (MS17-010) may be applicable if the host is running an unpatched Windows version.',
    ];
    const fullResponse = responses[Math.floor(Math.random() * responses.length)];
    const words = fullResponse.split(' ');
    for (let i = 0; i < words.length; i++) {
      await new Promise((r) => setTimeout(r, 30 + Math.random() * 50));
      this.onChatChunk(words.slice(0, i + 1).join(' ') + (i < words.length - 1 ? '' : ''));
    }
    this.onChatChunk(null); // signal done
  }

  disconnect() {
    this._running = false;
    this._clearTimers();
  }

  _schedule(ms, fn) {
    this._timers.push(setTimeout(fn, ms));
  }

  _clearTimers() {
    this._timers.forEach(clearTimeout);
    this._timers = [];
  }
}


/* ---------------------------------------------------------------------------
 * KingpinAgentAdapter — talks to the current Kingpin REST agent backend.
 * Uses the existing /engagement/offensive/{id}/ai-start etc. endpoints and
 * the scan-progress WebSocket. This is the "real but limited" adapter.
 * ------------------------------------------------------------------------- */

// Note: sendMessage is deliberately absent. It posted to /offensive/, a proxy
// to a self-hosted model that is no longer run; the endpoint has been removed.
// Flow interaction lives in PentAGIAdapter, which is what production uses
// (REACT_APP_AGENT_MODE=pentagi). The base class raises if called.
export class KingpinAgentAdapter extends AgentAdapter {
  constructor(opts) {
    super(opts);
    this._ws = null;
    this._executionId = null;
  }

  _headers() {
    const token = localStorage.getItem('token');
    return { access_token: `Bearer ${token}` };
  }

  async start(engagementId, config = {}) {
    try {
      const res = await axios.post(
        `${Config.APIBaseURL}/engagement/offensive/${engagementId}/ai-start`,
        config,
        { headers: this._headers(), withCredentials: true }
      );
      this._executionId = res.data?.execution_id;
      if (this._executionId) {
        this._connectWs(this._executionId);
      }
      return { executionId: this._executionId };
    } catch (e) {
      this.onError(e.response?.data?.detail || e.message);
      throw e;
    }
  }

  async pause(executionId) {
    const eid = executionId || this._executionId;
    if (!eid) return;
    try {
      // The current BE uses engagement_id, not execution_id for pause
      await axios.post(
        `${Config.APIBaseURL}/engagement/offensive/${eid}/ai-pause`,
        {},
        { headers: this._headers(), withCredentials: true }
      );
    } catch (e) {
      this.onError(e.response?.data?.detail || e.message);
    }
  }

  async stop(executionId) {
    const eid = executionId || this._executionId;
    if (!eid) return;
    try {
      await axios.post(
        `${Config.APIBaseURL}/engagement/offensive/${eid}/ai-stop`,
        {},
        { headers: this._headers(), withCredentials: true }
      );
      this.disconnect();
    } catch (e) {
      this.onError(e.response?.data?.detail || e.message);
    }
  }

  disconnect() {
    if (this._ws) {
      this._ws.close();
      this._ws = null;
    }
  }

  _connectWs(executionId) {
    const wsUrl = Config.APIBaseURL.replace(/^http/, 'ws');
    try {
      this._ws = new WebSocket(`${wsUrl}/ws/scan-progress/${executionId}`);
      this._ws.onopen = () => this.onEvent({ type: 'ws_connected', data: {} });
      this._ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          this.onEvent(parsed);
        } catch {
          /* ignore non-JSON messages */
        }
      };
      this._ws.onerror = () => this.onError('WebSocket connection error');
      this._ws.onclose = () => this.onEvent({ type: 'ws_disconnected', data: {} });
    } catch (e) {
      this.onError(`WebSocket failed: ${e.message}`);
    }
  }
}


/* ---------------------------------------------------------------------------
 * PentAGIAdapter — talks to PentAGI via Kingpin's backend proxy.
 *
 * All PentAGI API calls go through Kingpin's BE (/pentagi/*) so:
 *   - PentAGI credentials stay server-side
 *   - No CORS issues
 *   - Kingpin auto-generates instructions from engagement scope
 *   - Auto-import normalizes PentAGI output into Kingpin findings
 *
 * The adapter polls Kingpin's proxy endpoints for task/terminal updates
 * and maps them to the kill chain event contract.
 * ------------------------------------------------------------------------- */

export class PentAGIAdapter extends AgentAdapter {
  constructor(opts) {
    super(opts);
    this._flowId = null;
    this._engagementId = null;
    this._taskMap = new Map();
    this._completedTaskIds = new Set();
    this._taskCount = 0;
    this._completedTasks = 0;
    this._totalSubtasks = 0;
    this._completedSubtasks = 0;
    this._pollInterval = null;
    this._seenLogIds = new Set();
  }

  _headers() {
    const token = localStorage.getItem('token');
    return { access_token: `Bearer ${token}` };
  }

  async _api(method, path, data = null, timeout = 30000) {
    const opts = {
      headers: this._headers(),
      withCredentials: true,
      timeout,
    };
    let res;
    if (method === 'GET') {
      res = await axios.get(`${Config.APIBaseURL}${path}`, opts);
    } else {
      res = await axios.post(`${Config.APIBaseURL}${path}`, data, opts);
    }
    return res.data;
  }

  async start(engagementId, config = {}) {
    this._engagementId = engagementId;

    try {
      const data = await this._api('POST', `/pentagi/flow/start/${engagementId}`, {
        instruction: config.instruction || '',
      }, 120000);

      this._flowId = data.flow_id;
      this._taskMap.clear();
      this._completedTaskIds.clear();
      this._seenLogIds.clear();
      this._taskCount = 0;
      this._completedTasks = 0;

      this.onEvent({ type: 'scan_progress', data: { phase: 'Initializing', percent: 0, message: 'PentAGI flow created' } });

      this._startPolling();

      return { executionId: this._flowId };
    } catch (e) {
      console.error('[PentAGI] Start failed:', e);
      this.onError(e.message || 'Failed to create PentAGI flow');
      throw e;
    }
  }

  async reconnect(flowId) {
    this._flowId = flowId;
    this._taskMap.clear();
    this._completedTaskIds.clear();
    this._seenLogIds.clear();
    this._taskCount = 0;
    this._completedTasks = 0;
    this._totalSubtasks = 0;
    this._completedSubtasks = 0;

    this.onEvent({ type: 'scan_progress', data: { phase: 'Reconnecting', percent: 0, message: 'Reconnecting to flow...' } });
    this._startPolling();

    return { executionId: flowId };
  }

  async stop() {
    if (!this._flowId) return;
    try {
      await this._api('POST', `/pentagi/flow/${this._flowId}/stop`);
    } catch (e) {
      this.onError(e.message);
    }
    this.disconnect();
    this.onEvent({ type: 'scan_progress', data: { phase: 'Stopped', percent: 0, message: 'Flow stopped' } });
  }

  async pause() {
    this.onError('PentAGI does not support pause. Use stop instead.');
  }

  async resume() {
    this.onError('PentAGI does not support resume. Create a new flow.');
  }

  async sendMessage(message) {
    if (!this._flowId) { this.onError('No active flow'); return; }
    try {
      await this._api('POST', `/pentagi/flow/${this._flowId}/message`, { message });
      this.onChatChunk('Message sent to PentAGI. Watch the terminal for results.');
      this.onChatChunk(null);
    } catch (e) {
      this.onError(e.message);
    }
  }

  async importResults() {
    if (!this._flowId || !this._engagementId) return null;
    try {
      return await this._api('POST', `/pentagi/flow/${this._flowId}/import/${this._engagementId}`);
    } catch (e) {
      this.onError(e.message);
      return null;
    }
  }

  disconnect() {
    if (this._pollInterval) {
      clearInterval(this._pollInterval);
      this._pollInterval = null;
    }
  }

  // --- Internal: Map PentAGI subtask titles to MITRE ATT&CK tactics ---

  static TACTIC_KEYWORDS = [
    { id: 'TA0043', words: ['recon', 'information gathering', 'osint', 'enumerat', 'fingerprint', 'whois', 'dns', 'subdomain'] },
    { id: 'TA0042', words: ['resource dev', 'infrastructure', 'tool develop', 'staging'] },
    { id: 'TA0001', words: ['initial access', 'exploit', 'phish', 'public-facing', 'supply chain', 'vuln scan', 'nikto', 'nuclei', 'zap', 'dast', 'scanning'] },
    { id: 'TA0002', words: ['execut', 'command', 'script', 'injection', 'sql inject', 'xss', 'cross-site', 'input valid', 'rce', 'code exec'] },
    { id: 'TA0003', words: ['persist', 'implant', 'backdoor', 'scheduled task', 'startup'] },
    { id: 'TA0004', words: ['privilege', 'privesc', 'escalat', 'sudo', 'suid'] },
    { id: 'TA0005', words: ['evas', 'defense', 'obfuscat', 'bypass', 'antivirus', 'tamper'] },
    { id: 'TA0006', words: ['credential', 'password', 'brute force', 'auth', 'session', 'login', 'token', 'cookie', 'jwt'] },
    { id: 'TA0007', words: ['discover', 'service', 'port scan', 'nmap', 'network scan', 'mapping', 'crawl', 'spider', 'entry point', 'endpoint'] },
    { id: 'TA0008', words: ['lateral', 'pivot', 'remote', 'movement', 'spread'] },
    { id: 'TA0009', words: ['collect', 'exfil', 'data gather', 'harvest', 'scrape'] },
    { id: 'TA0011', words: ['c2', 'command and control', 'beacon', 'callback'] },
    { id: 'TA0010', words: ['exfiltrat', 'data transfer', 'upload', 'steal'] },
    { id: 'TA0040', words: ['impact', 'destruct', 'deface', 'ransom', 'denial'] },
    { id: null, words: ['config', 'deploy', 'ssl', 'tls', 'certificate', 'header', 'crypto', 'error handling', 'security config'] },
    { id: null, words: ['report', 'finding', 'verif', 'false positive', 'remediat', 'client-side', 'business logic', 'access control', 'authoriz'] },
  ];

  _mapSubtaskToTactic(title) {
    const lower = (title || '').toLowerCase();
    for (const { id, words } of PentAGIAdapter.TACTIC_KEYWORDS) {
      if (id && words.some(w => lower.includes(w))) return id;
    }
    return null;
  }

  _handleTaskRegistered(task) {
    this._taskMap.set(task.id, true);
  }

  _stripAnsi(text) {
    // eslint-disable-next-line no-control-regex
    return text.replace(/\u001b\[[0-9;]*m/g, '');
  }

  _handleTerminalLog(log) {
    const techId = log.subtaskId ? `tech-sub-${log.subtaskId}`
      : log.taskId ? `tech-${log.taskId}`
      : 'tech-main';

    const cleanText = this._stripAnsi(log.text || '');
    if (!cleanText.trim()) return;

    this.onEvent({
      type: 'technique_output',
      data: {
        technique_id: techId,
        output: cleanText,
      },
    });
  }

  _handleFlowUpdated(flow) {
    if (String(flow.id) !== String(this._flowId)) return;

    if (flow.status === 'finished' || flow.status === 'failed') {
      const isFailure = flow.status === 'failed';
      this.onEvent({ type: 'scan_progress', data: { phase: isFailure ? 'Failed' : 'Complete', percent: isFailure ? 0 : 100, message: isFailure ? 'Flow failed' : 'Assessment finished' } });
      if (!isFailure) this.onEvent({ type: 'engagement_completed', data: {} });
      else this.onError('PentAGI flow failed');
      this.disconnect();
      return;
    }

    // PentAGI flows often end in "waiting" when all work is done.
    // Detect completion by checking if all discovered subtasks are finished.
    if (flow.status === 'waiting' && this._totalSubtasks > 0 && this._completedSubtasks >= this._totalSubtasks) {
      this.onEvent({ type: 'scan_progress', data: { phase: 'Complete', percent: 100, message: 'Assessment finished' } });
      this.onEvent({ type: 'engagement_completed', data: {} });
      this.disconnect();
    }
  }

  _updateProgress(currentTask) {
    const total = this._totalSubtasks || this._taskCount || 1;
    const done = this._completedSubtasks || this._completedTasks || 0;
    const percent = Math.min(100, Math.round((done / total) * 100));
    this.onEvent({
      type: 'scan_progress',
      data: { phase: currentTask, percent, message: `${done}/${total} subtasks completed` },
    });
  }

  // --- Internal: Poll Kingpin proxy endpoints for updates ---

  _startPolling() {
    if (this._pollInterval) clearInterval(this._pollInterval);

    this._pollInterval = setInterval(async () => {
      if (!this._flowId) return;

      try {
        // Poll tasks via Kingpin proxy
        const tasks = await this._api('GET', `/pentagi/flow/${this._flowId}/tasks`);
        if (Array.isArray(tasks)) {
          // Recount total subtasks from authoritative server data each poll
          let totalSubs = 0;
          for (const t of tasks) totalSubs += (t.subtasks?.length || 0);
          this._totalSubtasks = totalSubs;

          for (const task of tasks) {
            // Register top-level task (no kill chain phase or technique)
            if (!this._taskMap.has(task.id)) {
              this._handleTaskRegistered(task);
            }

            // Subtasks are the real work units — each becomes a technique
            if (Array.isArray(task.subtasks) && task.subtasks.length > 0) {

              for (const sub of task.subtasks) {
                const subKey = `sub-${sub.id}`;
                const techId = `tech-sub-${sub.id}`;
                const tacticId = this._mapSubtaskToTactic(sub.title);

                // New subtask discovered — queue it as a technique
                if (!this._taskMap.has(subKey)) {
                  this._taskMap.set(subKey, tacticId);
                  this.onEvent({
                    type: 'technique_queued',
                    data: {
                      technique_id: techId,
                      tactic_id: tacticId || 'pentagi',
                      name: sub.title || `Subtask ${sub.id}`,
                      command: (sub.description || '').slice(0, 200),
                      tool: 'PentAGI',
                      agent: 'pentagi',
                    },
                  });
                }

                // Subtask started running — activate the mapped tactic
                if (sub.status === 'running' && !this._completedTaskIds.has(subKey + '-started')) {
                  this._completedTaskIds.add(subKey + '-started');
                  if (tacticId) {
                    this.onEvent({ type: 'tactic_started', data: { tactic_id: tacticId } });
                  }
                  this.onEvent({
                    type: 'technique_started',
                    data: { technique_id: techId, timestamp: new Date().toISOString() },
                  });
                  this._updateProgress(sub.title);
                }

                // Subtask completed — complete the tactic if no other subtask uses it
                if ((sub.status === 'finished' || sub.status === 'failed') && !this._completedTaskIds.has(subKey)) {
                  this._completedTaskIds.add(subKey);
                  this._completedSubtasks++;
                  if (tacticId) {
                    this.onEvent({
                      type: 'tactic_completed',
                      data: { tactic_id: tacticId },
                    });
                  }
                  this.onEvent({
                    type: 'technique_completed',
                    data: {
                      technique_id: techId,
                      tactic_id: tacticId || 'pentagi',
                      status: sub.status === 'failed' ? 'failed' : 'success',
                      timestamp: new Date().toISOString(),
                      findings: sub.result ? [{ title: sub.title, description: sub.result, severity: 'Info' }] : [],
                    },
                  });
                  this._updateProgress(sub.title);
                }
              }
            }

            // Top-level task completed — flow is done
            if ((task.status === 'finished' || task.status === 'failed') && !this._completedTaskIds.has(`task-${task.id}`)) {
              this._completedTaskIds.add(`task-${task.id}`);
            }
          }
        }

        // Poll terminal logs via Kingpin proxy
        const logs = await this._api('GET', `/pentagi/flow/${this._flowId}/terminal-logs`);
        if (Array.isArray(logs)) {
          for (const log of logs) {
            if (!this._seenLogIds.has(log.id)) {
              this._seenLogIds.add(log.id);
              this._handleTerminalLog(log);
            }
          }
        }

        // Poll flow status via Kingpin proxy
        const flow = await this._api('GET', `/pentagi/flow/${this._flowId}`);
        if (flow) {
          this._handleFlowUpdated(flow);
        }
      } catch (e) {
        console.error('[PentAGI] Poll error:', e.message, e);
      }
    }, 5000);
  }
}


/* ---------------------------------------------------------------------------
 * Factory — returns the right adapter based on configuration.
 *   REACT_APP_AGENT_MODE=mock    → MockAgentAdapter (default)
 *   REACT_APP_AGENT_MODE=live    → KingpinAgentAdapter
 *   REACT_APP_AGENT_MODE=pentagi → PentAGIAdapter
 * ------------------------------------------------------------------------- */

export function createAgentAdapter(callbacks) {
  const mode = process.env.REACT_APP_AGENT_MODE || 'mock';
  if (mode === 'pentagi') {
    return new PentAGIAdapter(callbacks);
  }
  if (mode === 'live') {
    return new KingpinAgentAdapter(callbacks);
  }
  return new MockAgentAdapter(callbacks);
}
